import { Injectable, computed, inject, signal } from '@angular/core';
import { AppService } from '../../core/app.service';
import { IssueApiService, IssueDeskUser } from '../../data/issues/issue-api.service';
import {
  Issue,
  IssueAnnotation,
  IssueEnv,
  IssueLocation,
  IssueSeverityId,
  IssueSnapshot,
  IssueStatusId,
  ISSUE_TEAM,
  issueIsOpen,
  issuePerson,
} from './issues.types';

/**
 * Signal-backed cache for the Issue Reporter feature. Reads are
 * hydrated from issue-service on init (Phase 1); writes fire the
 * matching REST call (Phase 2) with optimistic local updates so the
 * UI stays snappy while the request is in-flight.
 *
 * <p>Graceful degradation: if the backend errors (network down,
 * 403 for a non-triager on the tracker queue, etc) the optimistic
 * state stays put and the store surfaces a toast. A subsequent
 * {@link refresh} reconciles when the backend recovers.
 */
/**
 * Rows per scroll page.
 *
 * <p>Large enough that the first page fills the viewport (so the sentinel is not already visible
 * on load, which would fetch page 2 before the agent scrolls), small enough that the tracker
 * renders quickly on a long backlog.
 */
const PAGE_SIZE = 30;

@Injectable({ providedIn: 'root' })
export class IssuesStore {
  private static seq = 1043;
  private readonly api = inject(IssueApiService);
  private readonly app = inject(AppService);

  /** Seeded from the FE mock so the UI has something to render before
   *  the Phase-1 hydrate returns; refresh() overwrites with live data.
   *  Also the graceful-fallback path when issue-service is unreachable. */
  readonly issues = signal<Issue[]>(seedIssues());

  readonly openCount = computed(
    () => this.issues().filter((i) => issueIsOpen(i.status)).length,
  );

  /** Hydrated flag flips true after the first successful GET so screens
   *  know the local seed has been replaced. */
  readonly hydrated = signal<boolean>(false);

  /* ── Scroll paging ──────────────────────────────────────────────────────────
   * The queue is fetched a page at a time and appended as the agent scrolls, rather than pulling
   * the whole backlog up front. The filters the current page was fetched with are remembered, so
   * "load the next page" cannot silently mix rows from a previous filter set.
   */

  /** Highest page index already in the cache. */
  private readonly loadedPage = signal<number>(0);
  /** Does the server have more rows beyond what has been loaded? */
  readonly hasMore = signal<boolean>(false);
  /** A next-page fetch is in flight — guards against a scroll event firing twice. */
  readonly loadingMore = signal<boolean>(false);
  /** Total matching rows as the server counts them, for the "showing N of M" line. */
  readonly totalCount = signal<number>(0);
  /** Filters the loaded pages belong to; replayed for each next page. */
  private lastFilters: Record<string, unknown> = {};

  /** Live issue-desk roster from identity-service (real Developers +
   *  Technical Leaders) — the assignee dropdown's source of truth. Empty for a
   *  plain reporter (they can't read the directory endpoint) and when identity
   *  is unreachable; callers fall back to the FE mock team + denormalised names. */
  readonly directory = signal<IssueDeskUser[]>([]);

  /** High-watermark: the max {@link Issue#updatedAt} the store has
   *  ever ingested from the backend. Sent back as {@code updatedSince}
   *  on the next poll so the 30-second incremental fetch only pulls
   *  rows that actually changed. */
  private updatedSince: Date | null = null;

  /** Handle for the 30-second poll timer so it survives HMR + shuts
   *  down cleanly on {@link ngOnDestroy}. */
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  /** Poll cadence — matches the Phase 5 spec. Kept as a static so a
   *  future feature flag / tweak panel can lower it under demo load. */
  private static readonly POLL_INTERVAL_MS = 30_000;

  constructor() {
    this.refresh();
    this.loadDirectory();
    this.startPolling();
  }

  /** Pull the issue-desk directory once for the assignee dropdown. Best-effort:
   *  a non-triager gets an empty list (endpoint gated), so the dropdown — only
   *  shown to a Technical Leader anyway — just has no options besides Unassigned. */
  loadDirectory(): void {
    this.api.directory().subscribe((rows) => this.directory.set(rows ?? []));
  }

  /** A desk member by identity id (UUID sub), or undefined. */
  deskById(id: string | null | undefined): IssueDeskUser | undefined {
    if (!id) return undefined;
    return this.directory().find((u) => u.id === id);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ── read path ──────────────────────────────────────────────────

  /** Full refresh — no {@code updatedSince}, hard-replaces the cache.
   *  Uses server-side urgency sort so page 1 already carries the
   *  blockers + opens the tracker screen wants first. Tries the tracker
   *  queue first (Development-scope, holds every issue for a triager);
   *  if the caller can't triage falls back to {@code /mine}. Empty
   *  responses leave the seed in place so an offline dev keeps a demo
   *  dataset. */
  refresh(): void {
    this.refreshTracker({});
  }

  /** Refresh with an explicit filter set — the tracker screen calls
   *  this on filter changes so paging honours the current view rather
   *  than making the client sort through a full unfiltered queue.
   *
   *  <p>{@code status} is a single {@link IssueStatusId} — composite
   *  UI filters like {@code "open"} that map to multiple statuses stay
   *  client-side; pass {@code undefined} and let the tracker's
   *  {@code filtered()} narrow further. */
  refreshTracker(filters: {
    status?: IssueStatusId;
    severity?: IssueSeverityId;
    assignee?: string;   // user id, or the literal "none" for Unassigned
    q?: string;
  }): void {
    // A filter change restarts paging: page 0, and the cache is replaced rather than appended.
    this.lastFilters = { sort: 'urgency', ...filters };
    this.loadedPage.set(0);
    this.api.queue({ sort: 'urgency', ...filters, page: 0, size: PAGE_SIZE }).subscribe((page) => {
      if (page.items.length > 0) {
        this.hardReplace(page.items);
        this.hasMore.set(page.hasMore);
        this.totalCount.set(page.total);
        return;
      }
      this.hasMore.set(false);
      this.totalCount.set(0);
      // A triage-perm 403 lands as an empty page (client's catchError);
      // transparently fall through to /mine for reporters so their own
      // queue still populates.
      this.api.mine().subscribe((minePage) => {
        if (minePage.items.length > 0) this.hardReplace(minePage.items);
        // Mark hydrated even when both are empty (genuinely-empty queue), so the
        // screen drops its loading skeletons instead of spinning forever.
        else this.hydrated.set(true);
      });
    });
  }

  /**
   * Append the next page of the queue.
   *
   * <p>Appends rather than replaces, and re-sends the filters the current pages were fetched
   * with. No-op while a fetch is in flight or when the server says there is nothing left, so a
   * sentinel that stays on screen cannot spin the endpoint.
   */
  loadMoreTracker(): void {
    if (this.loadingMore() || !this.hasMore()) return;
    this.loadingMore.set(true);
    const next = this.loadedPage() + 1;
    this.api.queue({ ...this.lastFilters, page: next, size: PAGE_SIZE } as Parameters<IssueApiService['queue']>[0])
      .subscribe({
        next: (page) => {
          if (page.items.length > 0) {
            this.mergeIncoming(page.items);
            this.loadedPage.set(next);
          }
          // Trust the server's flag, but stop anyway on an empty page — otherwise a backend that
          // always reports `hasMore` would keep the sentinel firing forever.
          this.hasMore.set(page.hasMore && page.items.length > 0);
          if (page.total) this.totalCount.set(page.total);
          this.loadingMore.set(false);
        },
        error: () => { this.loadingMore.set(false); this.hasMore.set(false); },
      });
  }

  /** Hydrate the signed-in reporter's OWN issues (My Reported Issues). Hits the
   *  ungated {@code /mine} directly and MERGES the rows — so the reporter's list
   *  populates on a fresh load (logout + hard refresh, landing straight on the
   *  screen) without needing triage perms or a prior visit to the tracker. Merge
   *  (not hard-replace) so it never clobbers the tracker's cache or a just-created
   *  optimistic row; the created issue is persisted server-side and comes back here. */
  refreshMine(): void {
    this.api.mine().subscribe((page) => {
      this.mergeIncoming(page.items);
      this.hydrated.set(true);
    });
  }

  /** Incremental fetch — sends {@code updatedSince} so only rows with
   *  a newer {@code updatedAt} come back. Merged into the cache row-
   *  by-row (existing rows updated, new rows prepended). No-op when
   *  the store hasn't hydrated yet — {@link refresh} runs first. */
  private pollDelta(): void {
    if (!this.updatedSince) return;
    const since = this.updatedSince;
    // Try the tracker queue first (returns everything the caller can
    // see); a 403 for non-triagers still lands as an empty page thanks
    // to the client's catchError, so we transparently fall through to
    // /mine — which is ungated.
    this.api.queue({ updatedSince: since }).subscribe((page) => {
      if (page.items.length > 0) { this.mergeIncoming(page.items); return; }
      this.api.mine({ updatedSince: since }).subscribe((minePage) => {
        if (minePage.items.length > 0) this.mergeIncoming(minePage.items);
      });
    });
  }

  /** Replace the whole cache with the fresh page result. Bumps the
   *  {@link updatedSince} watermark to the newest row in the page. */
  private hardReplace(rows: Issue[]): void {
    this.issues.set(rows);
    this.hydrated.set(true);
    this.updatedSince = maxUpdatedAt(rows) ?? this.updatedSince;
  }

  /** Merge a delta into the cache — existing ids get updated in-place,
   *  new rows land at the top (matches the FE ordering intent: newest
   *  activity first). Advances {@link updatedSince}. */
  private mergeIncoming(rows: Issue[]): void {
    this.issues.update((list) => {
      const byId = new Map(list.map((r) => [r.id, r]));
      const fresh: Issue[] = [];
      for (const r of rows) {
        const existing = byId.get(r.id);
        if (existing) byId.set(r.id, mergeRow(existing, r));
        else fresh.push(r);
      }
      return [...fresh, ...Array.from(byId.values())];
    });
    const highWater = maxUpdatedAt(rows);
    if (highWater && (!this.updatedSince || highWater > this.updatedSince)) {
      this.updatedSince = highWater;
    }
  }

  /** Start the 30-second poll. Idempotent — a second call is a no-op. */
  startPolling(): void {
    if (this.pollTimer != null) return;
    this.pollTimer = setInterval(() => this.pollDelta(), IssuesStore.POLL_INTERVAL_MS);
  }

  stopPolling(): void {
    if (this.pollTimer != null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /** Load the full detail for one issue and merge into the cached row.
   *  Called when a queue row is selected; the summary lacks the thread /
   *  events / annotations that the detail pane needs. */
  refreshDetail(code: string): void {
    this.api.get(code).subscribe((live) => {
      if (!live) return;
      this.issues.update((list) => list.map((r) => (r.id === code ? { ...r, ...live } : r)));
    });
  }

  byId(id: string): Issue | undefined {
    return this.issues().find((i) => i.id === id);
  }

  forReporter(reporterId: string): Issue[] {
    return this.issues()
      .filter((i) => i.reporterId === reporterId)
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }

  // ── write path (Phase 2) ───────────────────────────────────────

  /** Raise a new issue.
   *
   *  - Optimistically prepends a local row so the reporter overlay's
   *    "ISS-xxxx reported" toast fires with a real-looking id.
   *  - Fires POST /issues in the background.
   *  - On success, swaps the temp id for the backend's ISS-code.
   *  - On error, leaves the optimistic row in place + toasts so the
   *    reporter doesn't lose their message. */
  create(data: {
    title: string;
    description: string;
    severity: IssueSeverityId;
    reporterId: string;
    location: IssueLocation;
    env: IssueEnv;
    snapshot: IssueSnapshot | null;
    /** Id returned by the eager attachment upload — the reporter
     *  overlay uploads the PNG the moment {@code html-to-image}
     *  produces a Blob, then hands the id here so the backend can
     *  link the pre-staged attachment row to the new issue. */
    snapshotAttachmentId?: string | null;
    annotations: IssueAnnotation[];
  }): Issue {
    const tempId = `ISS-${IssuesStore.seq++}`;
    const now = new Date();
    const me = issuePerson(data.reporterId);
    const optimistic: Issue = {
      id: tempId,
      title: data.title,
      description: data.description,
      severity: data.severity,
      status: 'New',
      rounds: 0,
      reporterId: data.reporterId,
      assignee: null,
      location: data.location,
      env: data.env,
      createdAt: now,
      updatedAt: now,
      snapshot: data.snapshot,
      annotations: data.annotations,
      events: [{ t: now, label: 'Issue reported', by: me?.name ?? 'Reporter' }],
      messages: [{
        id: `m${now.getTime()}`, side: 'reporter', author: data.reporterId,
        text: data.description, at: now,
      }],
    };
    this.issues.update((list) => [optimistic, ...list]);

    this.api.create({
      title: data.title,
      description: data.description,
      severity: data.severity,
      route: data.location.route,
      hash: data.location.hash,
      module: data.location.module,
      crumbs: data.location.crumbs,
      viewport: data.env.viewport,
      theme: data.env.theme,
      browser: data.env.browser,
      snapshotAttachmentId: data.snapshotAttachmentId ?? null,
      annotations: data.annotations.map((a): { kind: 'box' | 'arrow'; coords: Record<string, number> } =>
        a.type === 'box'
          ? { kind: 'box',   coords: { x: a.x, y: a.y, w: a.w, h: a.h } }
          : { kind: 'arrow', coords: { x1: a.x1, y1: a.y1, x2: a.x2, y2: a.y2 } }),
    }).subscribe({
      next: (live) => this.replace(tempId, live),
      error: () => this.app.toastKey('issues.toasts.syncFailed', undefined, 'warn'),
    });
    return optimistic;
  }

  post(id: string, side: 'reporter' | 'tech', author: string, text: string): void {
    const now = new Date();
    this.patch(id, (i) => ({
      ...i,
      messages: [...i.messages, { id: `m${now.getTime()}`, side, author, text, at: now }],
    }));
    // side is derived server-side from the caller's auth context; the
    // local {@code side} arg is used only for the optimistic bubble.
    this.api.postMessage(id, text).subscribe({
      next: (live) => this.replace(id, live),
      error: () => this.app.toastKey('issues.toasts.sendMessageFailed', undefined, 'warn'),
    });
  }

  setStatus(id: string, status: IssueStatusId, byName: string): void {
    const now = new Date();
    // Remembered for rollback. The optimistic patch below is what makes the control feel instant,
    // but the server can refuse a move (not a triager, or a no-op), and a toast on top of a status
    // the server never accepted leaves the screen stating something untrue until the next refetch.
    const prev = this.issues().find((i) => i.id === id)?.status ?? null;
    this.patch(id, (i) => ({
      ...i,
      status,
      events: [...i.events, { t: now, label: `Status → ${status}`, by: byName }],
      messages: [...i.messages, {
        id: `sy${now.getTime()}`, side: 'system',
        text: `Status changed to ${status} by ${byName}.`, at: now,
      }],
    }));
    this.api.setStatus(id, status).subscribe({
      next: (live) => this.replace(id, live),
      error: () => {
        // Undo the optimistic status and the two entries that announced it, so the history does
        // not keep a "Status → X" line for a change that never happened.
        if (prev) {
          this.patch(id, (i) => ({
            ...i,
            status: prev,
            events: i.events.filter((e) => e.t !== now),
            messages: i.messages.filter((m) => m.at !== now),
          }));
        }
        this.app.toastKey('issues.toasts.updateStatusFailed', undefined, 'warn');
      },
    });
  }

  assign(id: string, techId: string | null, byName: string): void {
    const now = new Date();
    // Resolve the picked developer's display name from the live directory, then
    // the FE mock (seeded ids), then the id — and denormalise it so every side
    // renders the owner without a lookup.
    const name = techId ? (this.deskById(techId)?.name ?? ISSUE_TEAM[techId]?.name ?? techId) : null;
    this.patch(id, (i) => ({
      ...i,
      assignee: techId,
      assigneeName: name ?? undefined,
      // First-assign auto-advances New → Triaged (matches the backend).
      status: i.status === 'New' && techId ? 'Triaged' : i.status,
      events: [...i.events, {
        t: now, label: techId ? `Assigned to ${name ?? '—'}` : 'Unassigned', by: byName,
      }],
      messages: [...i.messages, {
        id: `sy${now.getTime()}`, side: 'system',
        text: techId ? `Assigned to ${name ?? '—'} by ${byName}.` : `Unassigned by ${byName}.`,
        at: now,
      }],
    }));
    this.api.setAssignee(id, techId, name).subscribe({
      next: (live) => this.replace(id, live),
      error: () => this.app.toastKey('issues.toasts.assignFailed', undefined, 'warn'),
    });
  }

  setSeverity(id: string, sev: IssueSeverityId, byName: string): void {
    const now = new Date();
    this.patch(id, (i) => ({
      ...i,
      severity: sev,
      events: [...i.events, { t: now, label: `Severity → ${sev}`, by: byName }],
    }));
    this.api.setSeverity(id, sev).subscribe({
      next: (live) => this.replace(id, live),
      error: () => this.app.toastKey('issues.toasts.updateSeverityFailed', undefined, 'warn'),
    });
  }

  /** Reporter verifies a Fixed issue → Approved (closed). */
  approve(id: string, byName: string, text?: string): void {
    const now = new Date();
    this.patch(id, (i) => ({
      ...i,
      status: 'Approved',
      events: [...i.events, { t: now, label: 'Approved — issue closed', by: byName }],
      messages: [
        ...i.messages,
        ...(text ? [{ id: `m${now.getTime()}`, side: 'reporter' as const, author: i.reporterId, text, at: now }] : []),
        { id: `sy${now.getTime() + 1}`, side: 'system' as const, text: `Approved by ${byName}. Issue closed.`, at: now },
      ],
    }));
    this.api.approve(id, text).subscribe({
      next: (live) => this.replace(id, live),
      error: () => this.app.toastKey('issues.toasts.approveFailed', undefined, 'warn'),
    });
  }

  /** Reporter rejects a Fixed issue → back to In Progress, rounds++. */
  reject(id: string, byName: string, text: string): void {
    const now = new Date();
    this.patch(id, (i) => {
      const nextRound = (i.rounds ?? 0) + 2;
      return {
        ...i,
        status: 'In Progress',
        rounds: (i.rounds ?? 0) + 1,
        events: [...i.events, { t: now, label: `Rejected — reopened (round ${nextRound})`, by: byName }],
        messages: [
          ...i.messages,
          { id: `m${now.getTime()}`, side: 'reporter', author: i.reporterId, text, at: now },
          { id: `sy${now.getTime() + 1}`, side: 'system', text: `Reopened by ${byName} — back to In Progress (round ${nextRound}).`, at: now },
        ],
      };
    });
    this.api.reject(id, text).subscribe({
      next: (live) => this.replace(id, live),
      error: () => this.app.toastKey('issues.toasts.rejectFailed', undefined, 'warn'),
    });
  }

  // ── plumbing ───────────────────────────────────────────────────

  private patch(id: string, up: (i: Issue) => Issue): void {
    this.issues.update((list) =>
      list.map((i) => (i.id === id ? { ...up(i), updatedAt: new Date() } : i)),
    );
  }

  /** Replace the row at {@code oldId} with the canonical {@code live}
   *  wire result. Also handles the create-then-reconcile case where
   *  the optimistic row had a temp ISS-code and the backend returned
   *  a different one — the FE lookup keys off {@link Issue#id}, so
   *  the swap has to update both the position and the id. */
  private replace(oldId: string, live: Issue): void {
    this.issues.update((list) => {
      const idx = list.findIndex((r) => r.id === oldId);
      if (idx === -1) return [live, ...list];
      const next = list.slice();
      next[idx] = live;
      return next;
    });
  }
}

// ── helpers ──────────────────────────────────────────────────────────

/** Reduce a row list to its newest {@code updatedAt}. Null when the
 *  list is empty. */
function maxUpdatedAt(rows: Issue[]): Date | null {
  let hi: Date | null = null;
  for (const r of rows) {
    if (!hi || r.updatedAt > hi) hi = r.updatedAt;
  }
  return hi;
}

/** Merge a poll delta onto a cached row. The poll returns SUMMARY rows — empty
 *  thread / events / annotations and a blank description — so a plain replace
 *  would blank out a conversation already hydrated via the detail endpoint (the
 *  bug where a just-sent/received message vanished a few seconds later). Take the
 *  fresh scalar fields (status, severity, assignee, updatedAt, …) from the
 *  incoming row but keep the loaded detail whenever the delta doesn't carry it. */
function mergeRow(existing: Issue, incoming: Issue): Issue {
  return {
    ...existing,
    ...incoming,
    description: incoming.description || existing.description,
    messages:    incoming.messages.length    ? incoming.messages    : existing.messages,
    events:      incoming.events.length       ? incoming.events      : existing.events,
    annotations: incoming.annotations.length  ? incoming.annotations : existing.annotations,
  };
}

// ── seed ──────────────────────────────────────────────────────────────

function mins(m: number): Date { return new Date(Date.now() - m * 60_000); }

function seedIssues(): Issue[] {
  return [
    {
      id: 'ISS-1042',
      title: 'Seat map overlaps price panel on 13" screens',
      description: 'On the bus seat picker, when the browser width is around 1280px the seat grid overlaps the fare summary on the right. Can\'t click the last column of seats.',
      severity: 'Medium', status: 'Fixed', rounds: 0,
      reporterId: 'ahmed', assignee: 'nadia',
      location: { route: 'buses-passengers', crumbs: ['Inventory', 'Buses', 'Seats & Pax'], hash: '#buses-passengers', module: 'Buses · Seat picker' },
      env: { viewport: '1280 × 800', theme: 'light', browser: 'Chrome 126' },
      createdAt: mins(60 * 26), updatedAt: mins(90),
      snapshot: null,
      annotations: [{ type: 'box', x: 0.58, y: 0.28, w: 0.34, h: 0.4 }],
      events: [
        { t: mins(60 * 26), label: 'Issue reported', by: 'Ahmed Hassan' },
        { t: mins(60 * 24), label: 'Triaged · assigned to Nadia Cole', by: 'Ava Doyle' },
        { t: mins(60 * 20), label: 'Status → In Progress', by: 'Nadia Cole' },
        { t: mins(90), label: 'Status → Fixed', by: 'Nadia Cole' },
      ],
      messages: [
        { id: 'm1', side: 'reporter', author: 'ahmed', text: 'Last seat column is unclickable at this width.', at: mins(60 * 26) },
        { id: 'm2', side: 'system', text: 'Triaged and assigned to Nadia Cole (round 1).', at: mins(60 * 24) },
        { id: 'm3', side: 'tech', author: 'nadia', text: "Reproduced — the fare panel wasn't respecting the min-width. Pushed a fix, please re-check at 1280px.", at: mins(90) },
        { id: 'm4', side: 'system', text: 'Status changed to Fixed by Nadia Cole. Awaiting your verification.', at: mins(90) },
      ],
    },
    {
      id: 'ISS-1041',
      title: 'Wallet balance shows NaN after partial refund',
      description: "After processing a partial refund on VB-58207 the wallet ledger header briefly shows 'NaN' before refreshing. Screenshots attached.",
      severity: 'High', status: 'In Progress', rounds: 1,
      reporterId: 'omid', assignee: 'marcus',
      location: { route: 'wallet', crumbs: ['Finance', 'Wallet & Ledger'], hash: '#wallet', module: 'Finance · Wallet' },
      env: { viewport: '1512 × 945', theme: 'dark', browser: 'Safari 17' },
      createdAt: mins(60 * 5), updatedAt: mins(30),
      snapshot: null,
      annotations: [{ type: 'box', x: 0.06, y: 0.1, w: 0.3, h: 0.12 }],
      events: [
        { t: mins(60 * 5), label: 'Issue reported', by: 'Omid Rezaei' },
        { t: mins(60 * 4.6), label: 'Triaged · assigned to Marcus Reeve', by: 'Ava Doyle' },
        { t: mins(60 * 4), label: 'Status → In Progress', by: 'Marcus Reeve' },
        { t: mins(60 * 2), label: 'Status → Fixed', by: 'Marcus Reeve' },
        { t: mins(30), label: 'Rejected — reopened (round 2)', by: 'Omid Rezaei' },
      ],
      messages: [
        { id: 'm1', side: 'reporter', author: 'omid', text: 'Header flashes NaN right after a partial refund.', at: mins(60 * 5) },
        { id: 'm2', side: 'system', text: 'Triaged and assigned to Marcus Reeve (round 1).', at: mins(60 * 4.6) },
        { id: 'm3', side: 'tech', author: 'marcus', text: 'Added a guard for the intermediate value. Try again?', at: mins(60 * 2) },
        { id: 'm4', side: 'reporter', author: 'omid', text: 'Still happens if I refund twice quickly. Rejecting.', at: mins(30) },
        { id: 'm5', side: 'system', text: 'Reopened by Omid Rezaei — back to In Progress (round 2).', at: mins(30) },
      ],
    },
    {
      id: 'ISS-1040',
      title: "Date picker won't switch to Jalali on the manual flight entry",
      description: 'The dual calendar on Flights → Manual entry stays on Gregorian even after toggling. Toggle appears to do nothing.',
      severity: 'Blocker', status: 'New', rounds: 0,
      reporterId: 'ahmed', assignee: null,
      location: { route: 'flights-manual', crumbs: ['Inventory', 'Flights', 'Manual entry'], hash: '#flights-manual', module: 'Flights · Manual entry' },
      env: { viewport: '1440 × 900', theme: 'light', browser: 'Chrome 126' },
      createdAt: mins(45), updatedAt: mins(45),
      snapshot: null,
      annotations: [],
      events: [{ t: mins(45), label: 'Issue reported', by: 'Ahmed Hassan' }],
      messages: [
        { id: 'm1', side: 'reporter', author: 'ahmed', text: "Calendar toggle is stuck on Gregorian — can't enter the Solar Hijri departure date.", at: mins(45) },
      ],
    },
    {
      id: 'ISS-1038',
      title: 'Agency KYC status badge misaligned in dense mode',
      description: 'In compact density the KYC status pill sits half a line too high in the agencies table.',
      severity: 'Low', status: 'Approved', rounds: 0,
      reporterId: 'leila', assignee: 'nadia',
      location: { route: 'agencies', crumbs: ['Network', 'Agencies'], hash: '#agencies', module: 'Network · Agencies' },
      env: { viewport: '1680 × 1050', theme: 'light', browser: 'Firefox 128' },
      createdAt: mins(60 * 50), updatedAt: mins(60 * 40),
      snapshot: null,
      annotations: [],
      events: [
        { t: mins(60 * 50), label: 'Issue reported', by: 'Leila Farahani' },
        { t: mins(60 * 49), label: 'Triaged · assigned to Nadia Cole', by: 'Ava Doyle' },
        { t: mins(60 * 48), label: 'Status → In Progress', by: 'Nadia Cole' },
        { t: mins(60 * 45), label: 'Status → Fixed', by: 'Nadia Cole' },
        { t: mins(60 * 40), label: 'Approved — issue closed', by: 'Leila Farahani' },
      ],
      messages: [
        { id: 'm1', side: 'reporter', author: 'leila', text: 'Minor: KYC pill is misaligned in dense mode.', at: mins(60 * 50) },
        { id: 'm2', side: 'tech', author: 'nadia', text: 'Fixed the vertical-align on the badge.', at: mins(60 * 45) },
        { id: 'm3', side: 'reporter', author: 'leila', text: 'Looks perfect now, thanks!', at: mins(60 * 40) },
        { id: 'm4', side: 'system', text: 'Approved by Leila Farahani. Issue closed.', at: mins(60 * 40) },
      ],
    },
  ];
}
